import React, { type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import UploadForm from "#/components/app/UploadForm";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>
      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="mb-10 px-5 text-center text-5xl">
          Upload Healthcare Resume
        </h1>

        <section className="mx-3 space-y-5">
          <UploadForm />
        </section>

        <section className="mx-8 space-y-5">
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Step-by-step Guide to Uploading your Resume
          </h2>

          <StepsGuide />

          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Medical CV Writing Services
          </h2>

          <p>
            Embarking on a career in the healthcare industry requires more than
            just qualifications and experience; it demands a CV that speaks
            volumes about your professional journey and potential. This is where
            our Medical CV Writing Services come into play, serving as a
            catalyst for your career aspirations. Picture this: a CV that not
            only showcases your skills and accomplishments but does so in a
            manner that resonates with healthcare employers. Our specialized
            service is tailored to underline your unique value proposition,
            transforming your professional story into a compelling narrative.
            With industry-specific keywords and a format that appeals to medical
            staffing recruiters, your CV will stand out in a sea of applicants.
            It's not just a document; it's a strategic tool designed to elevate
            your profile and get you noticed in the highly competitive
            healthcare sector.
          </p>

          <p>
            Imagine the confidence you'll gain, knowing your CV has been crafted
            by experts familiar with the nuances of the medical field. Our
            service doesn't just polish your resume; it enhances it to reflect
            the very best of what you have to offer. We focus on accentuating
            your clinical experiences, research contributions, and any
            specialized training, ensuring these critical elements are not just
            included, but highlighted in the most impactful way. The result? A
            CV that not only aligns with your career goals but also speaks
            directly to the needs and expectations of healthcare employers. With
            our Medical CV Writing Services, you're not just preparing a job
            application; you're setting the stage for career advancement and
            opportunities that could define your future in healthcare. Let us
            help you open doors to a world of professional possibilities.
          </p>
        </section>
      </main>
      <Footer />
    </>
  );
}

interface StepProps {
  stepNumber: number;
  title: string;
  children: ReactNode;
}

// Step Component
const Step: React.FC<StepProps> = ({ stepNumber, title, children }) => {
  return (
    <div className="mb-8">
      <h3 className="mb-2 text-lg font-semibold">
        Step {stepNumber}: {title}
      </h3>
      <div className="border-l-2 border-gray-200 pl-4">{children}</div>
    </div>
  );
};
const StepsGuide = () => {
  return (
    <div className="container mx-auto p-4">
      <Step stepNumber={1} title="Prepare Your Resume">
        <p className="mb-2">
          Format Your Resume: Ensure your resume is in a widely accepted format,
          such as PDF or Microsoft Word (.doc or .docx).
        </p>
        <p className="mb-2">
          Update Your Content: Review your resume to ensure it includes your
          latest work experiences, certifications, and skills relevant to the
          healthcare industry.
        </p>
        <p>Proofread: Double-check for any typos or grammatical errors.</p>
      </Step>

      <Step stepNumber={2} title="Create or Log into Your Account">
        <p className="mb-2">
          Sign Up/Register: If you're new to the Roamdoc job search site, create
          an account.
        </p>
        <p>
          Log In: If you already have an account, simply log in with your
          credentials.
        </p>
      </Step>

      <Step stepNumber={3} title="Access the Resume Upload Section">
        <p>
          Navigate to Resume Upload: Once logged in, locate the ‘Upload Resume’
          or ‘My Profile’ section. This is usually found in your dashboard or
          under a tab labeled 'Resume' or 'CV'.
        </p>
      </Step>

      <Step stepNumber={4} title="Upload Your Resume">
        <p className="mb-2">
          Select Your Resume: Click on the ‘Upload Resume’ button. Browse your
          computer to find your resume file and select it.
        </p>
        <p className="mb-2">
          Review the Upload: After selecting your file, click ‘Open’ or ‘Upload’
          to add it to your profile. Wait for the confirmation that your upload
          was successful.
        </p>
        <p>
          Check Formatting: Once uploaded, preview your resume to ensure the
          formatting has remained consistent. Make any necessary adjustments.
        </p>
      </Step>

      <Step stepNumber={5} title="Complete Your Profile">
        <p className="mb-2">
          Fill in Details: Some job engines require additional information such
          as work history, education, and skills. Complete these sections to
          enhance your job search.
        </p>
        <p>
          Keywords: Incorporate relevant keywords into your profile and resume
          to improve visibility in employer searches.
        </p>
      </Step>

      <Step stepNumber={6} title="Privacy Settings">
        <p>
          Set Visibility: Choose who can see your resume. You can make it
          visible to all employers or only to those for specific job
          applications.
        </p>
      </Step>

      <Step stepNumber={7} title="Save and Confirm">
        <p className="mb-2">
          Finalize: Once everything is in place, save your changes.
        </p>
        <p>
          Confirmation: Look for a confirmation message or email that indicates
          your resume is successfully uploaded and visible to employers.
        </p>
      </Step>

      <Step stepNumber={8} title="Regular Updates">
        <p>
          Keep It Current: Regularly update your resume and profile with new
          skills, experiences, and certifications to maintain relevance.
        </p>
      </Step>
    </div>
  );
};

export const metadata: PageMetadata = {
  title: "Upload Healthcare Resume / CV & Apply to Hospital Jobs",
  description:
    "Upload your healthcare resume or CV to apply using Roamdoc as your job search site for healthcare professionals.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/upload-healthcare-resume`,
  },
};

export const Route = createFileRoute("/upload-healthcare-resume/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
